<template>
  <button
    class="bp-button"
    :disabled="_disabled"
    :class="buttonClasses"
    @click.prevent="onClick"
  >
    <div>
      <bp-icon :size="iconSize" v-if="icon" :icon="icon" />
    </div>
    <div class="bp-button-label" :class="labelClasses" v-if="showLabel">
      <slot>
        {{ label }}
      </slot>
    </div>
    <div class="divider" :class="dividerClasses" v-if="divider" />

    <div>
      <bp-icon :size="iconSize" v-if="trailingIcon" :icon="trailingIcon" />
    </div>

    <div class="bp-button-loading-icon" v-if="loading">
      <b-spinner class="bp-button-spinner" />
    </div>
  </button>
</template>
<script>
export default {
  props: {
    size: {
      type: String,
      default: "lg",
      validation: v => ["xl", "lg", "md", "sm"].includes(v)
    },
    color: {
      type: String,
      default: "primary",
      validation: v =>
        ["primary", "neutral", "positive", "negative", "alert"].includes(v)
    },
    variant: {
      type: String,
      default: "filled-2",
      validation: v =>
        ["filled-2", "filled-1", "outlined-1", "outlined", "glyph"].includes(v)
    },
    label: {
      type: String,
      default: ""
    },
    icon: {
      type: String
    },
    trailingIcon: {
      type: String
    },
    divider: {
      type: Boolean
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    alignLeft: {
      type: Boolean,
      default: false
    },
    route: {
      type: [String, Object],
      default: ""
    },
    block: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    buttonClasses() {
      const classes = [];

      classes.push(`bp-button-${this.color}-${this.variant}`);
      classes.push(`bp-button-${this.size}`);

      if (this.alignLeft) classes.push("justify-content-start");

      if (this.block) classes.push("w-100");

      if (this.loading) classes.push("loading");

      return classes;
    },
    iconSize() {
      const sizes = {
        sm: 14,
        md: 16,
        lg: 18,
        xl: 18
      };
      return sizes[this.size];
    },
    labelClasses() {
      const classes = [];

      const labelPadding = {
        sm: "px-3 px-lg-2",
        md: "px-4 px-lg-3",
        lg: "px-4",
        xl: "px-5 px-lg-4"
      };
      classes.push(labelPadding[this.size]);

      const labelFont = {
        sm: "functional-2",
        md: "functional-1"
      };
      if (labelFont[this.size]) classes.push(labelFont[this.size]);

      return classes;
    },
    dividerClasses() {
      const classes = [];

      const dividerPadding = {
        sm: "pr-1",
        md: "pr-1 pr-lg-2",
        lg: "pr-2 pr-lg-3",
        xl: "pr-3 pr-lg-4"
      };
      classes.push(dividerPadding[this.size]);

      return classes;
    },

    showLabel() {
      return !!this.label || !!this.$slots.default;
    },
    _disabled() {
      return this.disabled || this.loading;
    }
  },
  methods: {
    onClick() {
      if (this.route) {
        if (typeof this.route == "string")
          this.$router.push({ name: this.route });
        else this.$router.push(this.route);
      } else this.$emit("click");
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/buttons";
.bp-button {
  display: grid;
  border-radius: 4px;
  border: 1px solid transparent;
  grid-template-columns: auto auto auto auto;
  align-items: center;
  justify-content: center;
  position: relative;

  min-height: unset;
  min-width: unset;
  height: unset;
  width: unset;
  padding: 0;

  font-weight: 500;

  .bp-button-label {
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }

  .divider {
    margin-left: 1px;
    border-left: 1px solid;
    height: calc(100% - 3px);
  }

  &:disabled {
    color: rgba(192, 192, 192, 1) !important;
    background-color: rgba(215, 215, 215, 0.24) !important;
    border-color: transparent !important;
    .divider {
      border-color: rgba(192, 192, 192, 1) !important;
    }
  }

  &.loading {
    *:not(.bp-button-loading-icon, .bp-button-spinner) {
      opacity: 0;
    }
  }

  .bp-button-loading-icon {
    top: 50%;
    left: 50%;
    position: absolute;
    transform: translate(-50%, -50%);
    font-size: 12px;
  }
}
</style>
